import {makeAutoObservable} from 'mobx';
import {DateRangeValue, EFilterValueType} from 'webapp-lib/Components';

type FilterType<T, IsRequired> = T extends string
  ? IsRequired extends true
    ? T
    : T | ''
  : T;

type FilterNulledValueType<T, IsRequired> = IsRequired extends true
  ? T
  : T | undefined;
export interface IFilter<T, IsRequired extends boolean = false>
  extends EFilterValueType<T> {
  item: FilterType<T, IsRequired>;
  setElements: (values: FilterType<T, IsRequired>) => void;
  isEmpty: boolean;
}

/**
 * Filter class used to act as holder for information passed from filter components
 * Generic T should be a normal or typed string, or an array of normal/typed strings.
 *
 * If T is a typed string an additional value will be allowed, namely an empty string which acts as the empty value
 */
export class Filter<T, IsRequired extends boolean = false>
  implements IFilter<T, IsRequired>
{
  public item: FilterType<T, IsRequired>;
  private _defaultValue: FilterType<T, IsRequired>;

  public constructor(defaultFilter: FilterType<T, IsRequired>) {
    makeAutoObservable(this, {}, {autoBind: true});
    this.item = defaultFilter;
    this._defaultValue = defaultFilter;
  }

  public setElements(values: FilterType<T, IsRequired>): void {
    this.item = values;
  }

  public get isEmpty(): boolean {
    if (typeof this.item === 'string' || Array.isArray(this.item)) {
      return (
        this.item.length === 0 ||
        // special case for date range filters
        (Array.isArray(this.item) &&
          this.item.every((arrayItem) => arrayItem === undefined))
      );
    }
    return !this.item;
  }

  public isModified(): boolean {
    if ((typeof this.item) in ['string', 'number', 'boolean', 'undefined']) {
      return Boolean(this.item !== this._defaultValue);
    } else {
      return Boolean(
        JSON.stringify(this.item) !== JSON.stringify(this._defaultValue)
      );
    }
  }

  public reset(): void {
    this.item = this._defaultValue;
  }

  public get defaultValue(): T {
    return this._defaultValue as T;
  }

  public get valueOrDefault(): T {
    return (this.item as T) ?? (this._defaultValue as T);
  }

  /**
   * @return the filter or if not required undefined for an empty filter (empty string/array)
   */
  public get nulledValue(): FilterNulledValueType<T, IsRequired> {
    return (
      (typeof this.item === 'string' && this.item === '') ||
      (Array.isArray(this.item) && this.item.length === 0)
        ? undefined
        : this.item
    ) as FilterNulledValueType<T, IsRequired>;
  }
}

type DateRangeFilterType<IsRequired> = FilterType<DateRangeValue, IsRequired>;

export class DateRangeFilter<IsRequired extends boolean>
  implements IFilter<DateRangeValue, IsRequired>
{
  private readonly delegate: Filter<DateRangeValue, false>;

  public constructor(defaultFilter: DateRangeFilterType<IsRequired>) {
    makeAutoObservable(this, {}, {autoBind: true});
    this.delegate = new Filter<DateRangeValue, false>(defaultFilter);
  }

  public get isEmpty(): boolean {
    return this.delegate.isEmpty;
  }

  public get item(): DateRangeFilterType<IsRequired> {
    return this.delegate.item;
  }

  public set item(value: DateRangeFilterType<IsRequired>) {
    this.delegate.item = value;
  }

  public get nulledValue(): FilterNulledValueType<DateRangeValue, IsRequired> {
    return this.delegate.nulledValue as FilterNulledValueType<
      DateRangeValue,
      IsRequired
    >;
  }

  public setElements(values: DateRangeFilterType<IsRequired>): void {
    this.delegate.setElements(
      // conversion is necessary because we receive a null value when the filter
      // is restored from persistence (json does not support "undefined")
      [values[0] ?? undefined, values[1] ?? undefined]
    );
  }
}

export const singleOrUndefined = <T>(array: T[] | undefined): T | undefined => {
  if (array?.length !== 1) {
    return undefined;
  }

  return array[0];
};
